import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { PasswordInput } from "../components/PasswordInput";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { ArrowLeft, Key, Loader2, Eye, EyeOff, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserSettings() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const s = localStrings(language);

  const [currentPassword, setCurrentPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  // Weekly digest preference. null until the server has answered, so the
  // switch never shows a state that is a guess.
  const [digest, setDigest] = useState(null);
  const [digestSaving, setDigestSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/me/weekly-digest`, { withCredentials: true })
      .then((res) => setDigest(Boolean(res.data.enabled)))
      .catch(() => setDigest(false));
  }, []);

  const toggleDigest = async () => {
    if (digest === null || digestSaving) return;
    const next = !digest;
    setDigestSaving(true);
    setDigest(next); // optimistic: the switch answers the finger immediately
    try {
      await axios.patch(
        `${API}/me/weekly-digest`,
        { enabled: next },
        { withCredentials: true }
      );
      toast.success(next ? s("digest.on") : s("digest.off"));
    } catch {
      setDigest(!next);
      toast.error(s("digest.failed"));
    } finally {
      setDigestSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/me/weekly-digest/test`,
        {},
        { withCredentials: true }
      );
      toast.success(s("digest.testSent", { email: data.sent_to }));
    } catch (error) {
      toast.error(error.response?.data?.detail || s("digest.testFailed"));
    } finally {
      setTesting(false);
    }
  };

  const isValid =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword) &&
    newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setSaving(true);
    try {
      await axios.put(
        `${API}/users/me/password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(t("profile.passwordChanged"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("profile.passwordChangeFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("settings.backToHome")}
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="profile-title">
              {t("profile.title")}
            </h1>
            <p className="text-slate-500 mt-1">{t("profile.subtitle")}</p>
          </div>

          {/* User Info Card */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg shrink-0"
                style={{ backgroundColor: user?.color || "#3730A3" }}
                data-testid="profile-avatar"
              >
                {user?.display_name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900" data-testid="profile-name">{user?.display_name}</p>
                <p className="text-sm text-slate-500 truncate" data-testid="profile-email">{user?.email}</p>
                {/* Translated rather than printed as stored. The role is an
                    English word in the database, so the Italian profile page
                    said "admin" while the navbar menu, two clicks away, said
                    "Amministratore" for the same person. Same two keys the
                    navbar uses, so nothing was added to the translations. */}
                <p className="text-xs text-indigo-600 mt-0.5" data-testid="profile-role">
                  {user?.role === "admin" ? t("users.admin") : t("users.userRole")}
                </p>
              </div>
            </div>
          </div>

          {/* Weekly digest: one line and a switch. The setting says what it
              does; anything more would be explaining a checkbox. */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-6">
            <div className="px-6 py-5 flex items-center gap-4">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-indigo-600" />
              </div>

              <p className="text-sm text-slate-800 flex-1">{s("digest.label")}</p>

              <button
                type="button"
                role="switch"
                aria-checked={digest === true}
                aria-label={s("digest.label")}
                disabled={digest === null || digestSaving}
                onClick={toggleDigest}
                data-testid="digest-toggle"
                className={`relative w-11 h-6 rounded-full shrink-0 transition-colors disabled:opacity-50 ${
                  digest ? "bg-indigo-600" : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    digest ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>

            {/* Admin only, and it sends to this account alone: the address is
                taken from the session on the server, not passed from here. */}
            {user?.role === "admin" && (
              <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-4">
                <span className="text-xs text-slate-500">
                  {s("digest.testHint", { email: user?.email || "" })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendTest}
                  disabled={testing}
                  data-testid="digest-test-btn"
                  className="border-slate-200 text-slate-600 hover:text-slate-800 shrink-0"
                >
                  {testing ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {s("digest.testSend")}
                </Button>
              </div>
            )}
          </div>

          {/* Change Password Section */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                <Key className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900" data-testid="change-password-title">
                  {t("profile.changePassword")}
                </h2>
                <p className="text-xs text-slate-500">{t("profile.changePasswordDesc")}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5" data-testid="change-password-form">
              {/* Current Password */}
              <div className="space-y-2">
                <Label data-testid="current-password-label">{t("profile.currentPassword")}</Label>
                <div className="relative">
                  <Input
                    data-testid="current-password-input"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t("profile.currentPasswordPlaceholder")}
                    required
                    className="border-slate-200 pr-10"
                  />
                  <button
                    type="button"
                    data-testid="toggle-current-password-visibility"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* New Password + Confirm (reusable component) */}
              <PasswordInput
                password={newPassword}
                setPassword={setNewPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                required
              />

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  data-testid="save-password-btn"
                  disabled={saving || !isValid}
                  className="bg-indigo-700 hover:bg-indigo-600 text-white"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Key className="w-4 h-4 mr-2" />
                  )}
                  {saving ? t("profile.saving") : t("profile.savePassword")}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
